import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Paper, Stack, Typography } from '@mui/material';

import type { GetAudienceAdminListResponse } from '~/services/PromoService';
import { getDayJs } from '~/services/TimeService';

type AudienceAdminRecord = GetAudienceAdminListResponse['result']['audienceList'][number];

type Props = {
  audience: AudienceAdminRecord;
};

function splitList(values?: Array<string>): string {
  return values?.join(', ') ?? '';
}

function getUtmSummary(config: AudienceAdminRecord['config']): string {
  const parts = [
    ['UTM source', splitList(config?.utm?.source)],
    ['UTM medium', splitList(config?.utm?.medium)],
    ['UTM campaign', splitList(config?.utm?.campaign)],
    ['UTM term', splitList(config?.utm?.term)],
  ]
    .filter(([, value]) => Boolean(value))
    .map(([label, value]) => `${label}: ${value}`);

  return parts.join(' | ');
}

function isPastDateTime(value?: string | null): boolean {
  if (!value) {
    return false;
  }

  const date = getDayJs(value);
  return date.isValid() && date.isBefore(getDayJs());
}

export default function AudienceSummary({ audience }: Props) {
  const config = audience.config;
  const utmSummary = getUtmSummary(config);
  const startDateIsPast = isPastDateTime(audience.audienceStartDate);
  const endDateIsPast = isPastDateTime(audience.audienceEndDate);
  const conditionUser = config?.conditions?.user;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Accordion disableGutters elevation={0} defaultExpanded={false} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6">Audience details</Typography>
            {utmSummary ? (
              <Typography variant="body2" color="text.secondary">
                {utmSummary}
              </Typography>
            ) : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pb: 0 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={audience.enabled ? 'Enabled' : 'Disabled'}
                color={audience.enabled ? 'success' : 'default'}
              />
              <Chip label={audience.audienceType} />
              <Chip label={audience.syncModel} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Created by {audience.createdBy} on {audience.createdAt}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Brand: {audience.brand}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Audience ID: {audience.audienceId}
            </Typography>
            <Box>
              <Typography variant="subtitle2">Audience start date</Typography>
              <Typography variant="body2" color={startDateIsPast ? 'error.main' : 'text.primary'}>
                {audience.audienceStartDate || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">Audience end date</Typography>
              <Typography variant="body2" color={endDateIsPast ? 'error.main' : 'text.primary'}>
                {audience.audienceEndDate || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">UTM source</Typography>
              <Typography variant="body2">{splitList(config?.utm?.source) || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">UTM medium</Typography>
              <Typography variant="body2">{splitList(config?.utm?.medium) || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">UTM campaign</Typography>
              <Typography variant="body2">{splitList(config?.utm?.campaign) || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">UTM term</Typography>
              <Typography variant="body2">{splitList(config?.utm?.term) || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">User type</Typography>
              <Typography variant="body2">{config?.userType || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">Region</Typography>
              <Typography variant="body2">{config?.region?.join(', ') || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">User conditions</Typography>
              <Typography variant="body2">{conditionUser ? 'Configured' : '—'}</Typography>
              {conditionUser ? (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Lux Plus member required: {conditionUser.isLuxPlusMember ? 'Yes' : 'No'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Require user ID: {conditionUser.requiresUserId ? 'Yes' : 'No'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    User ID bucket: {conditionUser.userIdBucket || '—'}
                  </Typography>
                </Stack>
              ) : null}
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
